import { useState, useEffect, useRef } from "react";
import { LoginService } from "./LoginService";
import { Toast } from "primereact/toast";
import { Modal, Button } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Logout } from "../../utils/AccountUtils";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const AutoUpdateToken: React.FC = () => {
	const [lastActivityTime, setLastActivityTime] = useState(Date.now());
	const [showSessionDialog, setShowSessionDialog] = useState(false);
	const [loading, setLoading] = useState(false);
	const [expiryTimer, setExpiryTimer] = useState<NodeJS.Timeout | null>(null);
	const [dialogShownForCurrentExpiry, setDialogShownForCurrentExpiry] = useState(false);
	const toast = useRef<Toast>(null);
	const navigate = useNavigate();
	const [isLoggedIn, setIsLoggedIn] = useState(false);

	// Check if user is logged in
	useEffect(() => {
		const checkLoginStatus = () => {
			const token = sessionStorage.getItem("Token");
			setIsLoggedIn(!!token);
		};

		checkLoginStatus();
		// Also check for login status changes
		const intervalId = setInterval(checkLoginStatus, 5000);

		return () => {
			clearInterval(intervalId);
		};
	}, []);

	// JWT token handling
	const getTokenInfo = () => {
		const token = sessionStorage.getItem("Token");
		if (!token) return { isExpired: true, timeRemaining: 0, tokenExpiry: 0 };

		try {
			let decodedToken = jwtDecode<any>(token);
			let tokenExpiry = decodedToken.exp;
			let currentTime = Date.now();
			let timeRemaining = tokenExpiry * 1000 - currentTime;
			let isTokenExpired = timeRemaining < 0;

			return { isExpired: isTokenExpired, timeRemaining, tokenExpiry };
		} catch (error) {
			console.error("Error decoding token:", error);
			return { isExpired: true, timeRemaining: 0, tokenExpiry: 0 };
		}
	};

	// Calculate how much time before token expiration to show the dialog
	const getWarningThreshold = () => {
		// Show warning when 5% of the token's total lifetime is left, but at least 1 minute
		// and at most 5 minutes before expiration
		const tokenInfo = getTokenInfo();
		if (tokenInfo.isExpired) return 60 * 1000; // 1 minute default if expired

		const currentTime = Date.now();
		const totalLifetime = tokenInfo.tokenExpiry * 1000 - (currentTime - lastActivityTime);
		const warningThreshold = Math.min(
			Math.max(totalLifetime * 0.05, 60 * 1000), // at least 1 minute
			5 * 60 * 1000 // at most 5 minutes
		);

		return warningThreshold;
	};

	// Handler for "No" button - close the popup and set timer to logout when token expires
	const handleNoClick = () => {

		handleLogout()

		// Get the token info to determine when to auto-logout
		// const { timeRemaining } = getTokenInfo();

		// // Clear any existing timer
		// if (expiryTimer) {
		// 	clearTimeout(expiryTimer);
		// }

		// // Set timer to logout when token fully expires
		// const timer = setTimeout(() => {
		// 	toast.current?.show({
		// 		severity: "info",
		// 		summary: "Session expired",
		// 		detail: "Your session has expired. Please log in again.",
		// 		life: 3000
		// 	});
		// 	handleLogout();
		// }, timeRemaining + 1000); // Add 1 second buffer

		// setExpiryTimer(timer);
	};

	useEffect(() => {
		if (!isLoggedIn) return; // Skip if not logged in

		const handleActivity = () => {
			setLastActivityTime(Date.now());
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Tab") {
				handleActivity();
			}
		};

		document.addEventListener("mousemove", handleActivity);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("mousemove", handleActivity);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [isLoggedIn]);

	// Set up timer for automatic logout when token expires
	useEffect(() => {
		if (!isLoggedIn) return; // Skip if not logged in

		// Clear any existing timer when dialog visibility changes
		if (expiryTimer) {
			clearTimeout(expiryTimer);
			setExpiryTimer(null);
		}

		// If dialog is open, check how much time until token fully expires
		if (showSessionDialog) {
			const { isExpired, timeRemaining } = getTokenInfo();

			// If already expired, logout immediately
			if (isExpired) {
				handleLogout();
				return;
			}

			// Set a timer to automatically logout when token expires
			const timer = setTimeout(() => {
				toast.current?.show({
					severity: "info",
					summary: "Session expired",
					detail: "Your session has expired. Please log in again.",
					life: 3000
				});
				handleLogout();
			}, timeRemaining + 1000); // Add 1 second buffer

			setExpiryTimer(timer);
			setDialogShownForCurrentExpiry(true);
		}

		// Clean up timer on unmount or when dialog closes
		return () => {
			if (expiryTimer) {
				clearTimeout(expiryTimer);
			}
		};
	}, [showSessionDialog, isLoggedIn]);

	const updateToken = () => {
		if (!isLoggedIn) return; // Skip if not logged in

		setLoading(true);
		LoginService.updatetoken()
			.then((response) => {
				sessionStorage.setItem("User", JSON.stringify(response.data));
				sessionStorage.setItem("PartnerId", JSON.stringify(response.data.partnerId));
				sessionStorage.setItem("Token", JSON.stringify(response.data.jwtToken));
				setShowSessionDialog(false);
				setDialogShownForCurrentExpiry(false);
				// sessionStorage.setItem("StepFlag", "1");
				// sessionStorage.setItem("OnboardingStatus", "1");
			})
			.catch((error) => {
				setShowSessionDialog(false);
				if (error.response?.status === 500) {
					toast.current?.show({
						severity: "error",
						summary: "Something went wrong",
						life: 3000
					});
				} else if (error.response?.status === 401) {
					toast.current?.show({
						severity: "error",
						summary: "Unauthorized",
						life: 3000
					});
					Logout(navigate);
				} else if (error.response?.status === 400) {
					toast.current?.show({
						severity: "error",
						summary: error.response.data[0].errorMessage,
						life: 3000
					});
				} else {
					toast.current?.show({
						severity: "error",
						summary: "Error while getting updated token.",
						life: 3000
					});
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		if (!isLoggedIn) return; // Skip if not logged in

		// Check if token is expired
		const checkTokenExpiry = () => {
			const { isExpired, timeRemaining } = getTokenInfo();
			const warningThreshold = getWarningThreshold();

			// If already expired, logout immediately
			if (isExpired) {
				handleLogout();
				return;
			}

			// If token is close to expiring based on dynamic threshold and dialog hasn't been shown for this expiry
			if (timeRemaining < warningThreshold && !dialogShownForCurrentExpiry) {
				setShowSessionDialog(true);
			}
		};

		// Calculate timeout dynamically based on token expiration
		const tokenInfo = getTokenInfo();
		const inactivityThreshold = tokenInfo.timeRemaining * 0.25; // Check after 25% of remaining time has passed

		const inactivityTimeElapsed = Date.now() - lastActivityTime >= inactivityThreshold;

		if (inactivityTimeElapsed || tokenInfo.isExpired) {
			checkTokenExpiry();
		} else {
			// Set timeout to check before token expires
			const warningThreshold = getWarningThreshold();
			const timeUntilWarning = Math.max(tokenInfo.timeRemaining - warningThreshold, 5000); // At least 5 seconds

			const timeout = setTimeout(checkTokenExpiry, timeUntilWarning);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [lastActivityTime, navigate, dialogShownForCurrentExpiry, isLoggedIn]);

	const handleLogout = () => {
		setShowSessionDialog(false);
		setDialogShownForCurrentExpiry(false);
		Logout(navigate);
	};

	// If not logged in, don't render anything or run any logic
	if (!isLoggedIn) {
		return null;
	}

	return (
		<>
			<Toast ref={toast} />
			<Modal
				title="Session Timeout"
				open={showSessionDialog}
				width={400}
				maskClosable={false}
				closable={false}
				centered
				footer={[
					<Button key="logout" onClick={handleNoClick} style={{ height: "32px", marginRight: "8px" }}>
						No
					</Button>,
					<Button key="continue" type="primary" onClick={updateToken} autoFocus style={{ height: "32px" }} loading={loading}>
						Yes
					</Button>
				]}
			>
				<div className="flex align-items-center justify-content-center">
					<ExclamationCircleOutlined style={{ fontSize: "24px", color: "#faad14", marginRight: "12px" }} />
					<span>Your session is about to expire. Would you like to continue your session?</span>
				</div>
			</Modal>
		</>
	);
};

export default AutoUpdateToken;
